import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User, 
  onAuthStateChanged
} from 'firebase/auth';
import { 
  auth, 
  signInWithGoogle as firebaseSignInWithGoogle,
  logOut,
  createOrUpdateUserDocument,
  getUserProfile as fetchUserProfile
} from './firebase';
import { UserProfile } from './permissions';

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signInWithGoogle: () => Promise<User>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const loadUserProfile = async (user: User) => {
    try {
      const profile = await fetchUserProfile(user.uid);
      if (profile) {
        setUserProfile({
          role: profile.role || 'employee',
          isHR: profile.isHR || false,
          isSuperHR: profile.isSuperHR || false,
          managers: profile.managers || [],
          email: profile.email || user.email || '',
          displayName: profile.displayName || user.displayName || '',
        });
      } else {
        setUserProfile(null);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUserProfile(null);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        console.log('Auth state changed: User logged in', user.email);
        // Create or update the user document in Firestore
        try {
          await createOrUpdateUserDocument(user);
        } catch (error) {
          console.error('Error updating user document on auth state change:', error);
        }
        // Load user profile from Firestore
        await loadUserProfile(user);
      } else {
        console.log('Auth state changed: No user');
        setUserProfile(null);
      }
      
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signInWithGoogle = async () => {
    return await firebaseSignInWithGoogle();
  };

  const logout = async () => {
    await logOut();
  };

  const refreshProfile = async () => {
    if (currentUser) {
      await loadUserProfile(currentUser);
    }
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    signInWithGoogle,
    logout,
    refreshProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
